import React, { useState, useRef, useEffect } from 'react';
import { Chat } from '../types/chat';
import ChatMessage from './ChatMessage';
import { useTheme } from '../hooks/useTheme';
import sendMessageIconLight from '../assets/light/sendMessageIcon.svg';
import sendMessageIconDark from '../assets/dark/sendMessageIcon.svg';
import '../styles/AI.scss';

interface ChatAreaProps {
  activeChat: Chat | null;
  isLoading: boolean;
  sendMessage: (content: string) => Promise<void>;
}

const ChatArea: React.FC<ChatAreaProps> = ({ activeChat, isLoading, sendMessage }) => {
  const [message, setMessage] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const { theme } = useTheme();

  // Прокрутка вниз при появлении новых сообщений
  useEffect(() => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    }
  }, [activeChat?.messages]);

  // Автоматическая регулировка высоты textarea
  const adjustTextareaHeight = () => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = '40px';
      const scrollHeight = textarea.scrollHeight;
      textarea.style.height = `${Math.min(scrollHeight, 120)}px`;
    }
  };

  const handleMessageChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setMessage(e.target.value);
    adjustTextareaHeight();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (message.trim() && !isLoading) {
      await sendMessage(message);
      setMessage('');
      // Сбросить высоту textarea после отправки
      if (textareaRef.current) {
        textareaRef.current.style.height = '40px';
      }
    }
  };

  return (
    <div className="chat-area">
      {activeChat && activeChat.messages.length > 0 ? (
        <div className="messages-container" ref={messagesContainerRef}>
          {activeChat.messages.map(msg => (
            <ChatMessage key={msg.id} message={msg} />
          ))}
          <div ref={messagesEndRef} />
        </div>
      ) : (
        <div className="empty-state">
          <p>Начните новый разговор с ИИ</p>
        </div>
      )}
      
      <form className="input-area" onSubmit={handleSubmit}>
        <textarea
          ref={textareaRef}
          value={message}
          onChange={handleMessageChange}
          placeholder="Введите сообщение..."
          rows={1}
          disabled={isLoading}
          className="message-input"
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              handleSubmit(e);
            }
          }}
        />
        <img 
          src={theme === 'light' ? sendMessageIconLight : sendMessageIconDark} 
          alt="" 
          onClick={handleSubmit}
          style={{ 
            width: '40px', 
            height: '40px',
            cursor: 'pointer',
            opacity: !message.trim() || isLoading ? 0.5 : 1,
            marginLeft: '8px'
          }}
        />
      </form>
    </div>
  );
};

export default ChatArea; 